import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { TutaraButton } from '../tutara-ui';
import { getLandingNavigation } from '@/data/mockNavigation';
import { useAuthStore } from '@/stores/auth-store';

export default function LandingHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize dark mode from localStorage or system preference
  useEffect(() => {
    const isDark = localStorage.getItem('darkMode') === 'true' ||
      (!localStorage.getItem('darkMode') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    setIsDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('darkMode', String(newMode));
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Get navigation data from mock API
  const navLinks = getLandingNavigation();

  return (
    <>
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 dark:bg-tutara-dark-mode-surface/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/logos/logo-primary.png?v=2" 
              alt="Tutara" 
              className="h-10 w-auto block dark:hidden"
              onError={(e) => console.error('Failed to load logo-primary:', e)}
            />
            <img 
              src="/logos/logo-white.png?v=2" 
              alt="Tutara" 
              className="h-10 w-auto hidden dark:block"
              onError={(e) => console.error('Failed to load logo-white:', e)}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm font-medium transition-colors hover:text-tutara-primary ${
                  isScrolled 
                    ? 'text-tutara-charcoal dark:text-white' 
                    : 'text-tutara-charcoal dark:text-white'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5 text-yellow-500" />
              ) : (
                <Moon className="h-5 w-5 text-gray-600" />
              )}
            </button>
            
            {isAuthenticated ? (
              <Link to="/dashboard">
                <TutaraButton size="sm">
                  Dashboard
                </TutaraButton>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <button className={`px-4 py-2 text-sm font-medium transition-colors ${
                    isScrolled
                      ? 'text-tutara-charcoal dark:text-white hover:text-tutara-primary'
                      : 'text-tutara-charcoal dark:text-white hover:text-tutara-primary'
                  }`}>
                    Sign In
                  </button>
                </Link>
                <Link to="/signup">
                  <TutaraButton size="sm">
                    Get Started Free
                  </TutaraButton>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button and Dark Mode Toggle */}
          <div className="flex items-center space-x-2 lg:hidden">
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5 text-yellow-500" />
              ) : (
                <Moon className="h-5 w-5 text-gray-600" />
              )}
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-surface"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-tutara-charcoal dark:text-white" />
              ) : (
                <Menu className="h-6 w-6 text-tutara-charcoal dark:text-white" />
              )}
            </button>
          </div>
        </div>

      </nav>

    </header>

    {/* Mobile menu rendered via portal to escape header's stacking context */}
    {mobileMenuOpen && createPortal(
      <div className="lg:hidden fixed inset-0 z-[60]">
        {/* Backdrop overlay */}
        <div
          className="fixed inset-0 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        />
        {/* Menu panel */}
        <div className="fixed top-16 left-0 right-0 bg-white dark:bg-tutara-dark-mode-surface shadow-xl">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-tutara-charcoal dark:text-white hover:text-tutara-primary transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-4 border-t border-gray-200 dark:border-tutara-dark-mode-border space-y-3">
              {isAuthenticated ? (
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <TutaraButton className="w-full">
                    Dashboard
                  </TutaraButton>
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <button className="w-full px-4 py-2 text-base font-medium text-tutara-charcoal dark:text-white border border-gray-300 dark:border-tutara-dark-mode-border rounded-lg hover:bg-gray-50 dark:hover:bg-tutara-dark-mode-bg">
                      Sign In
                    </button>
                  </Link>
                  <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                    <TutaraButton className="w-full">
                      Get Started Free
                    </TutaraButton>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>,
      document.body
    )}
    </>
  );
}